import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import api from '../api';

// Types
export interface HealthStatus {
  status: string;
  timestamp: string;
  uptime: number;
  environment: string;
}

export interface ScraperStatus {
  running: boolean;
  lastResult: ScraperResult | null;
}

export interface ScraperResult {
  success: boolean;
  error?: string;
  timestamp: string;
  triggeredBy: string;
  practiceId?: string;
  result?: any;
}

export interface Clinician {
  _id: string;
  availabilityDate: string;
  clinicianName: string;
  scrapedAt: string;
  [key: string]: any;
}

export interface ClinicianAvailability {
  _id: string;
  clinicianId: string;
  clinicianName: string;
  email?: string;
  availabilityDate: string;
  availableSlots: any[];
  scrapedAt: string;
  practiceId?: string;
  [key: string]: any;
}

export interface ClinicianProfile {
  _id: string;
  id_practice: string;
  firstName: string;
  lastName: string;
  createdAt: string;
  [key: string]: any;
}

export interface ScraperRun {
  _id: string;
  runId: string;
  practiceId?: string;
  status: 'completed' | 'failed' | 'running';
  createdAt: string;
  fullResult?: any;
  [key: string]: any;
}

export interface ScrapedTeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  url?: string;
  is_clinician: boolean;
  titles?: string[];
}

export interface ScraperRunRecord {
  _id: string;
  id_practice: string;
  id_run: string;
  id_user?: string;
  status: string;
  status_code?: number;
  progress?: number;
  platform: string;
  scraperResult: ScrapedTeamMember[];
  team_member_count?: number;
  error?: string;
  created_at: number;
  updated_at: number;
}

export interface CronJob {
  id: number;
  name: string;
  description: string;
  schedule: string;
  practiceId?: string;
  status: 'created' | 'running' | 'stopped';
  created: string;
  lastStarted?: string;
  lastStopped?: string;
  lastRun?: string;
  isRunning?: boolean;
}

export interface ScraperState {
  // Health and Status
  health: {
    data: HealthStatus | null;
    loading: boolean;
    error: string | null;
  };
  status: {
    data: ScraperStatus | null;
    loading: boolean;
    error: string | null;
  };
  
  // Scraper Operations
  scraperRun: {
    loading: boolean;
    error: string | null;
    lastResult: ScraperResult | null;
  };
  scraperRunSync: {
    loading: boolean;
    error: string | null;
    result: ScraperResult | null;
  };
  imageScraper: {
    loading: boolean;
    error: string | null;
    result: ScraperResult | null;
  };
  bioScraper: {
    loading: boolean;
    error: string | null;
    result: ScraperResult | null;
  };
  
  // Simple Practice Connection
  simplePractice: {
    loading: boolean;
    error: string | null;
    data: any | null;
  };
  
  // Simple Practice Clinician Count
  simplePracticeClinicianCount: {
    loading: boolean;
    error: string | null;
    data: { count: number } | null;
  };
  
  // Simple Practice Scraper Connect
  simplePracticeScraperConnect: {
    loading: boolean;
    error: string | null;
    data: any | null;
  };
  
  // Simple Practice Clinician Import
  simplePracticeClinicianImport: {
    loading: boolean;
    error: string | null;
    data: any | null;
  };
  
  // Integration Creation with Scraper
  createIntegrationWithScraper: {
    loading: boolean;
    error: string | null;
    data: {
      integration: any | null;
      scraper: any | null;
    } | null;
  };
  
  // Data
  clinicians: {
    data: Clinician[];
    loading: boolean;
    error: string | null;
    count: number;
  };
  clinicianProfiles: {
    data: ClinicianProfile[];
    loading: boolean;
    error: string | null;
    count: number;
  };
  scraperRuns: {
    data: ScraperRun[];
    loading: boolean;
    error: string | null;
    count: number;
    summary: {
      totalRuns: number;
      successfulRuns: number;
      failedRuns: number;
    } | null;
  };
  scraperRunDetails: {
    data: ScraperRun | null;
    loading: boolean;
    error: string | null;
  };
  latestScraperRun: {
    data: ScraperRunRecord | null;
    loading: boolean;
    error: string | null;
  };

  // Cron Jobs
  cronJobs: {
    data: CronJob[];
    loading: boolean;
    error: string | null;
    totalJobs: number;
    activeJobs: number;
    stoppedJobs: number;
  };
  cronJobDetails: {
    data: CronJob | null;
    loading: boolean;
    error: string | null;
  };
  cronOperations: {
    create: { loading: boolean; error: string | null; };
    start: { loading: boolean; error: string | null; };
    stop: { loading: boolean; error: string | null; };
    delete: { loading: boolean; error: string | null; };
    trigger: { loading: boolean; error: string | null; };
  };
  
  // Clinician Availability with per-clinician invite states
  clinicianAvailability: {
    data: ClinicianAvailability[];
    loading: boolean;
    error: string | null;
    count: number;
    inviteLoading: { [key: string]: boolean };
    inviteErrors: { [key: string]: string | null };
  };
}

// API Base URL - adjust as needed
// const API_BASE_URL = 'http://18.216.137.240:3007';
const API_BASE_URL = 'http://localhost:3007';


// Async Thunks
export const fetchHealth = createAsyncThunk(
  'scraper/fetchHealth',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/health`);
      if (!response.ok) throw new Error('Failed to fetch health status');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const fetchConnection = createAsyncThunk(
  'scraper/fetchConnection',
  async (params: { practiceId?: string; username?: string; password?: string }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/scraper/connection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: Failed to fetch connection status`);
      }
      
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error occurred');
    }
  }
);

export const fetchScraperStatus = createAsyncThunk(
  'scraper/fetchStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/scraper/status`);
      if (!response.ok) throw new Error('Failed to fetch scraper status');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const runScraper = createAsyncThunk(
  'scraper/run',
  async (params: { practiceId?: string; username?: string; password?: string }, { rejectWithValue }) => {

    const IdPractice = JSON.parse(localStorage.getItem('user') || '{}')?.["custom:id_practice"];

    try {
      const response = await fetch(`${API_BASE_URL}/seeder/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          practiceId: IdPractice,
          username: params.username,
          password: params.password
        })
      });
      if (!response.ok) throw new Error('Failed to run scraper');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const runScraperSync = createAsyncThunk(
  'scraper/runSync',
  async (params: { practiceId?: string; username?: string; password?: string }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/seeder/run-sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          practiceId: params.practiceId,
          username: params.username,
          password: params.password
        })
      });
      if (!response.ok) throw new Error('Failed to run scraper sync');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const scrapeImages = createAsyncThunk(
  'scraper/scrapeImages',
  async (params: { practiceId?: string; clinicianId?: string; forceUpdate?: boolean }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/seeder/scrape-images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      });
      if (!response.ok) throw new Error('Failed to scrape images');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const scrapeBio = createAsyncThunk(
  'scraper/scrapeBio',
  async (practiceId: string, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/seeder/scrape-bio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ practiceId })
      });
      if (!response.ok) throw new Error('Failed to scrape bio');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const createIntegrationWithScraper = createAsyncThunk(
  'scraper/createIntegrationWithScraper',
  async (params: { 
    integration: any; 
    practiceId: string; 
    username: string; 
    password: string 
  }, { rejectWithValue }) => {
    try {
      

      // First create the integration
      const integrationResponse = await fetch(`${API_BASE_URL}/dev/scraper/simplepractice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params.integration)
      });
      
      if (!integrationResponse.ok) {
        const errorData = await integrationResponse.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${integrationResponse.status}: Failed to create integration`);
      }
      
      const integrationResult = await integrationResponse.json();
      
      // Then run the scraper
      const scraperResponse = await fetch(`${API_BASE_URL}/seeder/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          practiceId: params.practiceId,
          username: params.username,
          password: params.password
        })
      });
      
      if (!scraperResponse.ok) {
        throw new Error('Failed to run scraper after integration creation');
      }
      
      const scraperResult = await scraperResponse.json();
      
      return {
        integration: integrationResult,
        scraper: scraperResult
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error occurred');
    }
  }
);

export const fetchClinicians = createAsyncThunk(
  'scraper/fetchClinicians',
  async (params: { date?: string; clinician?: string; limit?: number } = {}, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params.date) searchParams.set('date', params.date);
      if (params.clinician) searchParams.set('clinician', params.clinician);
      if (params.limit) searchParams.set('limit', params.limit.toString());
      
      const response = await fetch(`${API_BASE_URL}/data/clinicians?${searchParams}`);
      if (!response.ok) throw new Error('Failed to fetch clinicians');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const fetchClinicianProfiles = createAsyncThunk(
  'scraper/fetchClinicianProfiles',
  async (params: { practiceId?: string; name?: string; limit?: number } = {}, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params.practiceId) searchParams.set('practiceId', params.practiceId);
      if (params.name) searchParams.set('name', params.name);
      if (params.limit) searchParams.set('limit', params.limit.toString());
      
      const response = await fetch(`${API_BASE_URL}/data/clinicians-profiles?${searchParams}`);
      if (!response.ok) throw new Error('Failed to fetch clinician profiles');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const fetchScraperRuns = createAsyncThunk(
  'scraper/fetchScraperRuns',
  async (params: { practiceId?: string; status?: string; limit?: number; includeFullResult?: boolean } = {}, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params.practiceId) searchParams.set('practiceId', params.practiceId);
      if (params.status) searchParams.set('status', params.status);
      if (params.limit) searchParams.set('limit', params.limit.toString());
      if (params.includeFullResult) searchParams.set('includeFullResult', 'true');
      
      const response = await fetch(`${API_BASE_URL}/data/scraper-runs?${searchParams}`);
      if (!response.ok) throw new Error('Failed to fetch scraper runs');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const fetchScraperRunDetails = createAsyncThunk(
  'scraper/fetchScraperRunDetails',
  async (runId: string, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/data/scraper-runs/${runId}`);
      if (!response.ok) throw new Error('Failed to fetch scraper run details');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const createCronJob = createAsyncThunk(
  'scraper/createCronJob',
  async (params: { schedule: string; practiceId?: string; name?: string; description?: string }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/cron/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      });
      if (!response.ok) throw new Error('Failed to create cron job');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const startCronJob = createAsyncThunk(
  'scraper/startCronJob',
  async (jobId: number, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/cron/start/${jobId}`, {
        method: 'POST'
      });
      if (!response.ok) throw new Error('Failed to start cron job');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const stopCronJob = createAsyncThunk(
  'scraper/stopCronJob',
  async (jobId: number, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/cron/stop/${jobId}`, {
        method: 'POST'
      });
      if (!response.ok) throw new Error('Failed to stop cron job');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const deleteCronJob = createAsyncThunk(
  'scraper/deleteCronJob',
  async (jobId: number, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/cron/delete/${jobId}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete cron job');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const fetchCronJobs = createAsyncThunk(
  'scraper/fetchCronJobs',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/cron/list`);
      if (!response.ok) throw new Error('Failed to fetch cron jobs');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const fetchCronJobDetails = createAsyncThunk(
  'scraper/fetchCronJobDetails',
  async (jobId: number, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/cron/details/${jobId}`);
      if (!response.ok) throw new Error('Failed to fetch cron job details');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const triggerCronJob = createAsyncThunk(
  'scraper/triggerCronJob',
  async (jobId: number, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/cron/trigger/${jobId}`, {
        method: 'POST'
      });
      if (!response.ok) throw new Error('Failed to trigger cron job');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const fetchClinicianAvailability = createAsyncThunk(
  'scraper/fetchClinicianAvailability',
  async (params: { date?: string; clinician?: string; practiceId?: string; limit?: number } = {}, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params.date) searchParams.set('date', params.date);
      if (params.clinician) searchParams.set('clinician', params.clinician);
      if (params.practiceId) searchParams.set('practiceId', params.practiceId);
      if (params.limit) searchParams.set('limit', params.limit.toString());
      
      const response = await fetch(`${API_BASE_URL}/data/clinician-availability?${searchParams}`);
      if (!response.ok) throw new Error('Failed to fetch clinician availability');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const sendClinicianInvite = createAsyncThunk(
  'scraper/sendClinicianInvite',
  async (params: { clinicianId: string; email: string; name: string }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/invites/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      });
      if (!response.ok) throw new Error('Failed to send clinician invite');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const connectSimplePractice = createAsyncThunk(
  'scraper/connectSimplePractice',
  async (params: {  id_practice: string; 
    id_user: string; 
    cluster: string ,
    taskdefinition: string }, { rejectWithValue }) => {
      try {
        const response = await axios.post(
          `${API_BASE_URL}/seeder/run-calendar-first`,
          params,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
  
        return response.data;
  
      } catch (error: any) {
        // Axios wraps errors in its own structure
        const message =
          error.response?.data?.message ||
          error.message ||
          'Unknown error occurred';
        return rejectWithValue(message);
      }
  }
);


export const getSimplePracticeClinicianCount = createAsyncThunk(
  'scraper/getSimplePracticeClinicianCount',
  async (params: { practice_id: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/data/team-clinicians-count-live-fixed', params);
      return response.data;
    } catch (error: any) {
      // Axios wraps errors in its own structure
      const message =
        error.response?.data?.message ||
        error.message ||
        'Unknown error occurred';
      return rejectWithValue(message);
    }
  }
);

export const connectSimplePracticeScraper = createAsyncThunk(
  'scraper/connectSimplePracticeScraper',
  async (params: { 
    options: string; 
    clinician_id?: string; 
    cognito_sub: string;
    id_practice: string;
    id_user: string;
    cluster: string;
    taskdefinition: string;
    containerName: string;
    enc_usr: string;
    enc_pswd: string;
  }, { rejectWithValue }) => {
    try {
      const response = await api.post('/scraper/simplepractice/connect', params);
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        error.message ||
        'Unknown error occurred';
      return rejectWithValue(message);
    }
  }
);

export const importSimplePracticeScraper = createAsyncThunk(
  'scraper/importSimplePracticeScraper',
  async (params: { 
    options: string; 
    clinician_id?: string; 
    cognito_sub: string;
    id_practice: string;
    id_user: string;
    cluster: string;
    taskdefinition: string;
    containerName: string;
    enc_usr: string;
    enc_pswd: string;
  }, { rejectWithValue }) => {
    try {
      const response = await api.post('/scraper/simplepractice/clinicians', params);
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        error.message ||
        'Unknown error occurred';
      return rejectWithValue(message);
    }
  }
);

export const fetchLatestScraperRun = createAsyncThunk(
  'scraper/fetchLatestScraperRun',
  async (params: { practiceId: string; platform?: string }, { rejectWithValue }) => {
    try {
      const { practiceId, platform = 'simple_practice' } = params;
      const response = await api.post(`/scraper-run/${practiceId}`, {
        filters: [
          {
            id_practice: practiceId,
            platform
          }
        ],
        options: {
          page_size: 1,
          sort_fields: [
            { created_at: -1 }
          ]
        }
      });
      const body = response.data?.body;
      const run = Array.isArray(body) && body.length > 0 ? body[0] : null;
      if (run) {
        // scraperResult arrives as a flat array (current API) or { body: [...] } (older runs)
        const sr: any = run.scraperResult;
        run.scraperResult = Array.isArray(sr) ? sr : sr?.body || [];
      }
      return run as ScraperRunRecord | null;
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        error.message ||
        'Unknown error occurred';
      return rejectWithValue(message);
    }
  }
);

// Initial State
const initialState: ScraperState = {
  health: { data: null, loading: false, error: null },
  status: { data: null, loading: false, error: null },
  scraperRun: { loading: false, error: null, lastResult: null },
  scraperRunSync: { loading: false, error: null, result: null },
  imageScraper: { loading: false, error: null, result: null },
  bioScraper: { loading: false, error: null, result: null },
  simplePractice: { loading: false, error: null, data: null },
  simplePracticeClinicianCount: { loading: false, error: null, data: null },
  simplePracticeScraperConnect: { loading: false, error: null, data: null },
  simplePracticeClinicianImport: { loading: false, error: null, data: null },
  clinicians: { data: [], loading: false, error: null, count: 0 },
  clinicianProfiles: { data: [], loading: false, error: null, count: 0 },
  scraperRuns: { data: [], loading: false, error: null, count: 0, summary: null },
  scraperRunDetails: { data: null, loading: false, error: null },
  latestScraperRun: { data: null, loading: false, error: null },
  cronJobs: { data: [], loading: false, error: null, totalJobs: 0, activeJobs: 0, stoppedJobs: 0 },
  cronJobDetails: { data: null, loading: false, error: null },
  clinicianAvailability: { 
    data: [], 
    loading: false, 
    error: null, 
    count: 0, 
    inviteLoading: {}, 
    inviteErrors: {} 
  },
  cronOperations: {
    create: { loading: false, error: null },
    start: { loading: false, error: null },
    stop: { loading: false, error: null },
    delete: { loading: false, error: null },
    trigger: { loading: false, error: null }
  },
  createIntegrationWithScraper: {
    loading: false,
    error: null,
    data: null
  }
};

// Slice
const scraperSlice = createSlice({
  name: 'scraper',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.health.error = null;
      state.status.error = null;
      state.scraperRun.error = null;
      state.scraperRunSync.error = null;
      state.imageScraper.error = null;
      state.bioScraper.error = null;
      state.simplePractice.error = null;
      state.simplePracticeScraperConnect.error = null;
      state.simplePracticeClinicianImport.error = null;
      state.clinicians.error = null;
      state.clinicianProfiles.error = null;
      state.scraperRuns.error = null;
      state.scraperRunDetails.error = null;
      state.latestScraperRun.error = null;
      state.cronJobs.error = null;
      state.cronJobDetails.error = null;
      state.clinicianAvailability.error = null;
      state.clinicianAvailability.inviteErrors = {};
      Object.keys(state.cronOperations).forEach(key => {
        state.cronOperations[key as keyof typeof state.cronOperations].error = null;
      });
    },
    resetScraperResults: (state) => {
      state.scraperRun.lastResult = null;
      state.scraperRunSync.result = null;
      state.imageScraper.result = null;
      state.bioScraper.result = null;
    }
  },
  extraReducers: (builder) => {
    // Health
    builder
      .addCase(fetchHealth.pending, (state) => {
        state.health.loading = true;
        state.health.error = null;
      })
      .addCase(fetchHealth.fulfilled, (state, action) => {
        state.health.loading = false;
        state.health.data = action.payload;
      })
      .addCase(fetchHealth.rejected, (state, action) => {
        state.health.loading = false;
        state.health.error = action.payload as string;
      });

    // Status
    builder
      .addCase(fetchScraperStatus.pending, (state) => {
        state.status.loading = true;
        state.status.error = null;
      })
      .addCase(fetchScraperStatus.fulfilled, (state, action) => {
        state.status.loading = false;
        state.status.data = action.payload;
      })
      .addCase(fetchScraperStatus.rejected, (state, action) => {
        state.status.loading = false;
        state.status.error = action.payload as string;
      });

    // Connection
    builder
      .addCase(fetchConnection.pending, (state) => {
        state.status.loading = true;
        state.status.error = null;
      })
      .addCase(fetchConnection.fulfilled, (state, action) => {
        state.status.loading = false;
        state.status.data = action.payload;
      })
      .addCase(fetchConnection.rejected, (state, action) => {
        state.status.loading = false;
        state.status.error = action.payload as string;
      });

    // Scraper Run
    builder
      .addCase(runScraper.pending, (state) => {
        state.scraperRun.loading = true;
        state.scraperRun.error = null;
      })
      .addCase(runScraper.fulfilled, (state, action) => {
        state.scraperRun.loading = false;
        state.scraperRun.lastResult = action.payload;
      })
      .addCase(runScraper.rejected, (state, action) => {
        state.scraperRun.loading = false;
        state.scraperRun.error = action.payload as string;
      });

    // Scraper Run Sync
    builder
      .addCase(runScraperSync.pending, (state) => {
        state.scraperRunSync.loading = true;
        state.scraperRunSync.error = null;
      })
      .addCase(runScraperSync.fulfilled, (state, action) => {
        state.scraperRunSync.loading = false;
        state.scraperRunSync.result = action.payload;
      })
      .addCase(runScraperSync.rejected, (state, action) => {
        state.scraperRunSync.loading = false;
        state.scraperRunSync.error = action.payload as string;
      });

    // Scrape Images
    builder
      .addCase(scrapeImages.pending, (state) => {
        state.imageScraper.loading = true;
        state.imageScraper.error = null;
      })
      .addCase(scrapeImages.fulfilled, (state, action) => {
        state.imageScraper.loading = false;
        state.imageScraper.result = action.payload;
      })
      .addCase(scrapeImages.rejected, (state, action) => {
        state.imageScraper.loading = false;
        state.imageScraper.error = action.payload as string;
      });

    // Scrape Bio
    builder
      .addCase(scrapeBio.pending, (state) => {
        state.bioScraper.loading = true;
        state.bioScraper.error = null;
      })
      .addCase(scrapeBio.fulfilled, (state, action) => {
        state.bioScraper.loading = false;
        state.bioScraper.result = action.payload;
      })
      .addCase(scrapeBio.rejected, (state, action) => {
        state.bioScraper.loading = false;
        state.bioScraper.error = action.payload as string;
      });

    // Clinicians
    builder
      .addCase(fetchClinicians.pending, (state) => {
        state.clinicians.loading = true;
        state.clinicians.error = null;
      })
      .addCase(fetchClinicians.fulfilled, (state, action) => {
        state.clinicians.loading = false;
        state.clinicians.data = action.payload.data;
        state.clinicians.count = action.payload.count;
      })
      .addCase(fetchClinicians.rejected, (state, action) => {
        state.clinicians.loading = false;
        state.clinicians.error = action.payload as string;
      });

    // Clinician Profiles
    builder
      .addCase(fetchClinicianProfiles.pending, (state) => {
        state.clinicianProfiles.loading = true;
        state.clinicianProfiles.error = null;
      })
      .addCase(fetchClinicianProfiles.fulfilled, (state, action) => {
        state.clinicianProfiles.loading = false;
        state.clinicianProfiles.data = action.payload.data;
        state.clinicianProfiles.count = action.payload.count;
      })
      .addCase(fetchClinicianProfiles.rejected, (state, action) => {
        state.clinicianProfiles.loading = false;
        state.clinicianProfiles.error = action.payload as string;
      });

    // Scraper Runs
    builder
      .addCase(fetchScraperRuns.pending, (state) => {
        state.scraperRuns.loading = true;
        state.scraperRuns.error = null;
      })
      .addCase(fetchScraperRuns.fulfilled, (state, action) => {
        state.scraperRuns.loading = false;
        state.scraperRuns.data = action.payload.runs;
        state.scraperRuns.count = action.payload.count;
        state.scraperRuns.summary = action.payload.summary;
      })
      .addCase(fetchScraperRuns.rejected, (state, action) => {
        state.scraperRuns.loading = false;
        state.scraperRuns.error = action.payload as string;
      });

    // Scraper Run Details
    builder
      .addCase(fetchScraperRunDetails.pending, (state) => {
        state.scraperRunDetails.loading = true;
        state.scraperRunDetails.error = null;
      })
      .addCase(fetchScraperRunDetails.fulfilled, (state, action) => {
        state.scraperRunDetails.loading = false;
        state.scraperRunDetails.data = action.payload.run;
      })
      .addCase(fetchScraperRunDetails.rejected, (state, action) => {
        state.scraperRunDetails.loading = false;
        state.scraperRunDetails.error = action.payload as string;
      });

    // Cron Jobs
    builder
      .addCase(fetchCronJobs.pending, (state) => {
        state.cronJobs.loading = true;
        state.cronJobs.error = null;
      })
      .addCase(fetchCronJobs.fulfilled, (state, action) => {
        state.cronJobs.loading = false;
        state.cronJobs.data = action.payload.jobs;
        state.cronJobs.totalJobs = action.payload.totalJobs;
        state.cronJobs.activeJobs = action.payload.activeJobs;
        state.cronJobs.stoppedJobs = action.payload.stoppedJobs;
      })
      .addCase(fetchCronJobs.rejected, (state, action) => {
        state.cronJobs.loading = false;
        state.cronJobs.error = action.payload as string;
      });

    // Cron Job Details
    builder
      .addCase(fetchCronJobDetails.pending, (state) => {
        state.cronJobDetails.loading = true;
        state.cronJobDetails.error = null;
      })
      .addCase(fetchCronJobDetails.fulfilled, (state, action) => {
        state.cronJobDetails.loading = false;
        state.cronJobDetails.data = action.payload.job;
      })
      .addCase(fetchCronJobDetails.rejected, (state, action) => {
        state.cronJobDetails.loading = false;
        state.cronJobDetails.error = action.payload as string;
      });

    // Create Cron Job
    builder
      .addCase(createCronJob.pending, (state) => {
        state.cronOperations.create.loading = true;
        state.cronOperations.create.error = null;
      })
      .addCase(createCronJob.fulfilled, (state) => {
        state.cronOperations.create.loading = false;
      })
      .addCase(createCronJob.rejected, (state, action) => {
        state.cronOperations.create.loading = false;
        state.cronOperations.create.error = action.payload as string;
      });

    // Start Cron Job
    builder
      .addCase(startCronJob.pending, (state) => {
        state.cronOperations.start.loading = true;
        state.cronOperations.start.error = null;
      })
      .addCase(startCronJob.fulfilled, (state) => {
        state.cronOperations.start.loading = false;
      })
      .addCase(startCronJob.rejected, (state, action) => {
        state.cronOperations.start.loading = false;
        state.cronOperations.start.error = action.payload as string;
      });

    // Stop Cron Job
    builder
      .addCase(stopCronJob.pending, (state) => {
        state.cronOperations.stop.loading = true;
        state.cronOperations.stop.error = null;
      })
      .addCase(stopCronJob.fulfilled, (state) => {
        state.cronOperations.stop.loading = false;
      })
      .addCase(stopCronJob.rejected, (state, action) => {
        state.cronOperations.stop.loading = false;
        state.cronOperations.stop.error = action.payload as string;
      });

    // Delete Cron Job
    builder
      .addCase(deleteCronJob.pending, (state) => {
        state.cronOperations.delete.loading = true;
        state.cronOperations.delete.error = null;
      })
      .addCase(deleteCronJob.fulfilled, (state) => {
        state.cronOperations.delete.loading = false;
      })
      .addCase(deleteCronJob.rejected, (state, action) => {
        state.cronOperations.delete.loading = false;
        state.cronOperations.delete.error = action.payload as string;
      });

    // Trigger Cron Job
    builder
      .addCase(triggerCronJob.pending, (state) => {
        state.cronOperations.trigger.loading = true;
        state.cronOperations.trigger.error = null;
      })
      .addCase(triggerCronJob.fulfilled, (state) => {
        state.cronOperations.trigger.loading = false;
      })
      .addCase(triggerCronJob.rejected, (state, action) => {
        state.cronOperations.trigger.loading = false;
        state.cronOperations.trigger.error = action.payload as string;
      });

    // Clinician Availability
    builder
      .addCase(fetchClinicianAvailability.pending, (state) => {
        state.clinicianAvailability.loading = true;
        state.clinicianAvailability.error = null;
      })
      .addCase(fetchClinicianAvailability.fulfilled, (state, action) => {
        state.clinicianAvailability.loading = false;
        state.clinicianAvailability.data = action.payload.data;
        state.clinicianAvailability.count = action.payload.count;
      })
      .addCase(fetchClinicianAvailability.rejected, (state, action) => {
        state.clinicianAvailability.loading = false;
        state.clinicianAvailability.error = action.payload as string;
      });

    // Send Clinician Invite
    builder
      .addCase(sendClinicianInvite.pending, (state, action) => {
        const { clinicianId } = action.meta.arg;
        state.clinicianAvailability.inviteLoading[clinicianId] = true;
        state.clinicianAvailability.inviteErrors[clinicianId] = null;
      })
      .addCase(sendClinicianInvite.fulfilled, (state, action) => {
        const { clinicianId } = action.meta.arg;
        state.clinicianAvailability.inviteLoading[clinicianId] = false;
      })
      .addCase(sendClinicianInvite.rejected, (state, action) => {
        const { clinicianId } = action.meta.arg;
        state.clinicianAvailability.inviteLoading[clinicianId] = false;
        state.clinicianAvailability.inviteErrors[clinicianId] = action.payload as string;
      });

    // Connect Simple Practice
    builder
      .addCase(connectSimplePractice.pending, (state) => {
        state.simplePractice.loading = true;
        state.simplePractice.error = null;
      })
      .addCase(connectSimplePractice.fulfilled, (state, action) => {
        state.simplePractice.loading = false;
        state.simplePractice.data = action.payload;
      })
      .addCase(connectSimplePractice.rejected, (state, action) => {
        state.simplePractice.loading = false;
        state.simplePractice.error = action.payload as string;
      });

    // Get Simple Practice Clinician Count
    builder
      .addCase(getSimplePracticeClinicianCount.pending, (state) => {
        state.simplePracticeClinicianCount.loading = true;
        state.simplePracticeClinicianCount.error = null;
      })
      .addCase(getSimplePracticeClinicianCount.fulfilled, (state, action) => {
        state.simplePracticeClinicianCount.loading = false;
        state.simplePracticeClinicianCount.data = action.payload;
      })
      .addCase(getSimplePracticeClinicianCount.rejected, (state, action) => {
        state.simplePracticeClinicianCount.loading = false;
        state.simplePracticeClinicianCount.error = action.payload as string;
      });

    // Connect Simple Practice Scraper
    builder
      .addCase(connectSimplePracticeScraper.pending, (state) => {
        state.simplePracticeScraperConnect.loading = true;
        state.simplePracticeScraperConnect.error = null;
      })
      .addCase(connectSimplePracticeScraper.fulfilled, (state, action) => {
        state.simplePracticeScraperConnect.loading = false;
        state.simplePracticeScraperConnect.data = action.payload;
      })
      .addCase(connectSimplePracticeScraper.rejected, (state, action) => {
        state.simplePracticeScraperConnect.loading = false;
        state.simplePracticeScraperConnect.error = action.payload as string;
      });

    // Import Simple Practice Clinicians
    builder
      .addCase(importSimplePracticeScraper.pending, (state) => {
        state.simplePracticeClinicianImport.loading = true;
        state.simplePracticeClinicianImport.error = null;
      })
      .addCase(importSimplePracticeScraper.fulfilled, (state, action) => {
        state.simplePracticeClinicianImport.loading = false;
        state.simplePracticeClinicianImport.data = action.payload;
      })
      .addCase(importSimplePracticeScraper.rejected, (state, action) => {
        state.simplePracticeClinicianImport.loading = false;
        state.simplePracticeClinicianImport.error = action.payload as string;
      });

    // Latest Scraper Run
    builder
      .addCase(fetchLatestScraperRun.pending, (state) => {
        state.latestScraperRun.loading = true;
        state.latestScraperRun.error = null;
      })
      .addCase(fetchLatestScraperRun.fulfilled, (state, action) => {
        state.latestScraperRun.loading = false;
        state.latestScraperRun.data = action.payload;
      })
      .addCase(fetchLatestScraperRun.rejected, (state, action) => {
        state.latestScraperRun.loading = false;
        state.latestScraperRun.error = action.payload as string;
      });

    // Create Integration with Scraper
    builder
      .addCase(createIntegrationWithScraper.pending, (state) => {
        state.createIntegrationWithScraper.loading = true;
        state.createIntegrationWithScraper.error = null;
      })
      .addCase(createIntegrationWithScraper.fulfilled, (state, action) => {
        state.createIntegrationWithScraper.loading = false;
        state.createIntegrationWithScraper.data = action.payload;
      })
      .addCase(createIntegrationWithScraper.rejected, (state, action) => {
        state.createIntegrationWithScraper.loading = false;
        state.createIntegrationWithScraper.error = action.payload as string;
      });
  }
});

// Actions
export const { clearErrors, resetScraperResults } = scraperSlice.actions;

export default scraperSlice.reducer;   
